import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { AuthContextType, User, AuthCredentials, RegisterCredentials } from '../types';
import { 
  authLogin, 
  authRegister, 
  authMe, 
  authUpgrade, 
  updateProfile, 
  changePassword, 
  deleteUserAccount,
  forgotPassword,
  resetPassword 
} from '../services/api';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('auth_token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is authenticated on app load
    const checkAuth = async () => {
      const storedToken = localStorage.getItem('auth_token');
      if (storedToken) {
        try {
          const userData = await authMe();
          setUser(userData);
          setToken(storedToken);
        } catch (error) {
          // Token is invalid, remove it
          localStorage.removeItem('auth_token');
          setToken(null);
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (credentials: AuthCredentials) => {
    try {
      const response = await authLogin(credentials);
      setUser(response.user);
      setToken(response.token);
      localStorage.setItem('auth_token', response.token);
    } catch (error) {
      throw error;
    }
  };

  const register = async (credentials: RegisterCredentials) => {
    try {
      const response = await authRegister(credentials);
      setUser(response.user);
      setToken(response.token);
      localStorage.setItem('auth_token', response.token);
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('auth_token');
  };

  const upgrade = async () => {
    try {
      const updatedUser = await authUpgrade();
      setUser(updatedUser);
    } catch (error) {
      throw error;
    }
  };

  // Refresh user data from backend (useful after email verification or subscription changes)
  const refreshUser = async () => {
    try {
      const userData = await authMe();
      setUser(userData);
      return userData;
    } catch (error) {
      console.error('Failed to refresh user:', error);
      throw error;
    }
  };

  // Update user profile (name/email)
  const updateUserProfile = async (data: { name?: string; email?: string }) => {
    try {
      const response = await updateProfile(data);
      setUser(response.user);
    } catch (error) {
      console.error('Failed to update profile:', error);
      throw error;
    }
  };

  // Change password (for authenticated users)
  const changeUserPassword = async (currentPassword: string, newPassword: string) => {
    try {
      await changePassword(currentPassword, newPassword);
    } catch (error) {
      console.error('Failed to change password:', error);
      throw error;
    }
  };

  // Delete user account
  const deleteUserAccountFn = async () => {
    try {
      await deleteUserAccount();
      // Clear local state after account deletion
      setUser(null);
      setToken(null);
      localStorage.removeItem('auth_token');
    } catch (error) {
      console.error('Failed to delete account:', error);
      throw error;
    }
  };

  // Request password reset (unauthenticated)
  const requestPasswordReset = async (email: string) => {
    try {
      await forgotPassword(email);
    } catch (error) {
      console.error('Failed to request password reset:', error);
      throw error;
    }
  };

  // Reset password with token (unauthenticated)
  const resetUserPassword = async (token: string, password: string) => {
    try {
      await resetPassword(token, password);
    } catch (error) {
      console.error('Failed to reset password:', error);
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    token,
    login,
    register,
    logout,
    isAuthenticated: !!user,
    isEmailVerified: user?.emailVerified ?? false,
    loading,
    upgrade,
    refreshUser,
    updateUserProfile,
    changeUserPassword,
    deleteUserAccount: deleteUserAccountFn,
    requestPasswordReset,
    resetUserPassword
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
